import { HttpClient, HttpErrorResponse, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { filter, firstValueFrom, timeout } from 'rxjs';
import { isNetworkError } from './network-error';
import { EnqueueInput, OfflineStore } from './offline-store.service';
import { OutboxEntry } from './offline.models';
import { OutboxHandlerRegistry, isDoneStatus } from './outbox-handler';
import { replaceLocalIds } from './sync-engine.service';

/**
 * interrupted: the caller queues the same body. key is the Idempotency-Key of a request that may have reached the
 * server; null when nothing was sent (unresolved local id): the entry then stays a never-sent one (mergeable, discardable).
 */
export type OnlineOutcome = { kind: 'done'; body: unknown } | { kind: 'interrupted'; key: string | null };

/** no answer, or the server cannot answer now (5xx, 429): the same key and body go into the outbox (spec §6, step 3 FE-Review H8) */
export function isRetryableOnline(error: unknown): boolean {
  return isNetworkError(error) || (error instanceof HttpErrorResponse && (error.status >= 500 || error.status === 429));
}

/**
 * Online writes of the modules (Aufgaben since step 3, Dokumente and Pins since step 4) through the handler of the
 * type: same URL and body as a later outbox send, with a new Idempotency-Key, without the outbox marker (no deadline
 * tolerance online, Q4). A network error, 5xx or 429 returns the key: the caller queues the same body under it
 * (spec §6, plan D7 of step 3). Any other error is thrown. Moved out of TaskOfflineService (step 4, plan Task 1).
 */
@Injectable({ providedIn: 'root' })
export class OnlineWriter {
  private readonly http = inject(HttpClient);
  private readonly store = inject(OfflineStore);
  private readonly registry = inject(OutboxHandlerRegistry);

  /** timeoutMs: without a file the whole request, with a file the longest time without progress */
  async send(input: EnqueueInput, timeoutMs: number): Promise<OnlineOutcome> {
    this.store.assertWritable();
    const key = crypto.randomUUID();
    const handler = this.registry.get(input.type);
    if (!handler) throw new Error(`no outbox handler for ${input.type}`);
    const file = input.blobs?.[0];
    const userId = input.userId ?? this.store.currentUserId();
    // E2 gate FE-G11: a local id never goes to the server. Known ones are replaced with the id map of the user; an
    // unresolved one (its create is still queued) returns the key like a cut-off, so the caller queues the same body.
    const resolvedPayload = replaceLocalIds(input.payload, await this.store.idMap(userId));
    if (resolvedPayload.missing.length > 0) return { kind: 'interrupted', key: null };
    const payload = resolvedPayload.value as Record<string, unknown>;
    const probe: OutboxEntry = {
      opId: 'online',
      idempotencyKey: key,
      userId: userId ?? '',
      type: input.type,
      entityRef: input.entityRef,
      // file handlers read their file through ctx.getBlob(blobIds[0]); online the file is passed directly
      payload: file ? { ...payload, blobIds: ['online'] } : payload,
      dependsOn: [],
      status: 'running',
      attempts: 0,
      nextAttemptAt: 0,
      lastError: null,
      conflict: null,
      createdAt: Date.now(),
    };
    const request = await handler.build(probe, {
      resolveId: (id) => this.store.resolveId(id, userId),
      getBlob: async () => file,
    });
    // second line: a handler that puts an unresolved local id into the url or a JSON body is stopped as well
    if (carriesLocalId(request)) return { kind: 'interrupted', key: null };
    try {
      // with a file: progress events, and the timeout counts only a standstill (a slow uplink still gets through, Rev1 W1)
      const sent = request.clone({ setHeaders: { 'Idempotency-Key': key }, reportProgress: !!file });
      const response = await firstValueFrom(
        this.http.request(sent).pipe(
          timeout(timeoutMs),
          filter((event): event is HttpResponse<unknown> => event.type === HttpEventType.Response),
        ),
      );
      return { kind: 'done', body: response.body };
    } catch (error) {
      if (error instanceof HttpErrorResponse && isDoneStatus(handler, probe, error.status, error.error)) return { kind: 'done', body: null };
      if (isRetryableOnline(error)) return { kind: 'interrupted', key };
      throw error;
    }
  }
}

/** url path still holds a local id, or a value of the JSON body is one (same exact-value rule as the outbox, plan C9) */
function carriesLocalId(request: HttpRequest<unknown>): boolean {
  const body = request.body;
  if (body && typeof body === 'object' && !(body instanceof FormData) && !(body instanceof Blob)) {
    if (replaceLocalIds(body, new Map()).missing.length > 0) return true;
  }
  return /\/local(:|%3A)[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/|\?|$)/i.test(request.url);
}
